/**
 * Class to handle getting and setting caret and selections in input and textarea elements
 */
export class CaretManagerQuill {
	/**
	 * Create an instance to manage the given contentEditable element
	 * @param {Element} contentEditable  The contentEditable element to manage
	 */
	constructor(contentEditable, mentionable = null) {
		/**
		 * @type {Element} contentEditable  The contentEditable whose caret to manage
		 */
		this.contentEditable = contentEditable;
		this.quill = contentEditable.parentNode.__quill;
		if (!this.quill) {
			throw Error('Quill instance not found for contentEditable');
		}
		if (mentionable) {
			this.quill.keyboard.bindings[13].unshift({
				key: 13,
				context: {},
				handler: () => {
					// return false to avoid a newline if we have just chosen an option
					if (mentionable.activeItem) {
						mentionable.suggestions = [];
						return false;
					}
					return true;
				},
			});
		}
	}

	/**
	 * Get the start and end positions of the current word
	 * @returns {Object}  An object with start, end and word
	 */
	getWordInfo() {
		const selection = this._getSelection();
		if (!selection) {
			return this._lastWord || null;
		}
		const text = selection.anchorNode.nodeValue || '';
		const len = text.length;
		let start = selection.anchorOffset;
		let end = selection.focusOffset - 1;
		if (/\s/.test(text.substring(start - 1, start))) {
			start++;
			end++;
		}
		while (start-- > 0 && !/\s/.test(text.substring(start - 1, start)));
		while (end++ < len && !/\s/.test(text.substring(end - 1, end)));
		if (/\s/.test(text.substring(end - 1, end))) {
			end--;
		}
		if (start < 0) {
			start = 0;
		}
		if (end > len) {
			end = len;
		}
		const word = text.substring(start, end);
		const blot = selection.blot;
		const anchorNode = selection.anchorNode;
		this._lastWord = { start, end, word, blot, anchorNode };
		return this._lastWord;
	}

	/**
	 * Get the current word
	 * @returns {String|null}
	 */
	getWord() {
		const info = this.getWordInfo();
		return info ? info.word : null;
	}

	/**
	 * Set the current word
	 * @param {String} newWord  The text to insert into the textarea
	 * @return {Boolean}  True if set ok
	 */
	setWord(newWord) {
		const info = this._lastWord;
		if (!info) {
			return false;
		}
		const { start, end, anchorNode, blot } = info;
		blot.deleteAt(start, end - start);
		blot.insertAt(start, newWord + ' ');
		document
			.getSelection()
			.setBaseAndExtent(anchorNode, start + newWord.length + 1, anchorNode, start + newWord.length + 1);
		return true;
	}

	/**
	 * Get the bounding client rect for the current word
	 * @returns {Object|null}
	 */
	getWordRect() {
		const info = this._lastWord;
		if (!info) {
			return false;
		}
		const { start, end, anchorNode } = info;
		const range = document.createRange();
		range.setStart(anchorNode, start);
		range.setEnd(anchorNode, end);
		let { top, left, width, height } = range.getBoundingClientRect();
		top += (window.scrollY || window.pageYOffset) + height;
		left += window.scrollX || window.pageXOffset;
		return { top, left, width, height };
	}

	getWordSuggestionPosition(relativeToElement) {
		let { top, left } = this.getWordRect();
		const thisRect = relativeToElement.getBoundingClientRect();
		top -= thisRect.top;
		top -= window.scrollY || window.pageYOffset;
		top += 2; // a little room between text and mention box
		left -= thisRect.left;
		return {
			top: top + 'px',
			left: left + 'px',
		};
	}

	/**
	 * Get the selection (or last selection) if from inside our contentEditable
	 * @returns {Selection|null}
	 * @private
	 */
	_getSelection() {
		const selection = document.getSelection();
		if (!selection || !selection.anchorNode || selection.rangeCount > 1) {
			return null;
		} else if (selection.anchorNode.__blot) {
			this._lastSelection = selection;
			this._lastSelection.blot = selection.anchorNode.__blot.blot;
			return selection;
		} else if (this._lastSelection) {
			return this._lastSelection;
		} else {
			return null;
		}
	}
}
